import type { TechnologyStatus } from '@/types/technology'

/* Status pill for a technology-section capability.
 *
 * The three states carry real meaning and the colour has to make that legible
 * at a glance without a legend:
 *
 *   live      emerald  — usable today. The only state that reads as "yes".
 *   building  brand orange — under way. Warm, active, clearly not finished.
 *   planned   muted white — on the roadmap and nothing more. Deliberately the
 *             quietest of the three so a roadmap item can never be skimmed as
 *             a shipped one.
 *
 * The `status` KEY drives the styling, not the label, so relabelling in
 * TechnologyContent::STATUS_LABELS never touches this file.
 *
 *
 * Shape and type are the marketing site's own badge (`.marketing-pricing-badge`):
 * a pill with a brand-orange wash, white letter-spaced caps, 0.72rem / 800.
 * "In build" wears exactly that; "Roadmap" is the same pill in neutral; the
 * dot carries the state colour. Nothing is smaller than 0.72rem.
 */

const STYLES: Record<TechnologyStatus, string> = {
  live: 'border-emerald-400/35 bg-emerald-400/10 text-white',
  building: 'border-[#ff5400]/35 bg-[#ff5400]/[0.16] text-white',
  planned: 'border-white/[0.14] bg-white/[0.05] text-white/70',
}

const DOT: Record<TechnologyStatus, string> = {
  live: 'bg-emerald-400',
  building: 'bg-[#ff6a1a]',
  planned: 'bg-white/40',
}

export default function StatusBadge({
  status,
  label,
  className = '',
}: {
  status: TechnologyStatus
  label: string
  className?: string
}) {
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-[0.72rem] font-extrabold uppercase tracking-[0.18em] ${STYLES[status]} ${className}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${DOT[status]}`} aria-hidden="true" />
      {label}
    </span>
  )
}
